<template>
    <div>
        <SToolbar>
            <v-icon small class="mr-1">ic-20 ic-info ic-col-grey</v-icon>
            <v-toolbar-title>{{ $t('banner') }}</v-toolbar-title>
        </SToolbar>
        <v-skeleton-loader v-if="!record" type="article, actions" />
        <v-card v-else height="var(--usable-height)" class="main-grid">
            <LoadingOverlay :value="saving" absolute />

            <!-- Header with Buttons -->
            <div class="top-header">
                <light-toolbar dense>
                    <v-toolbar-title
                        >{{ $t('eintrag_bearbeiten') }}: {{ record.title }} | {{ $t(record.type) }}</v-toolbar-title
                    >
                </light-toolbar>
                <v-toolbar dense class="elevation-0" color="primary white--text">
                    <v-slide-group next-icon="ic-20 ic-next ic-col-white" prev-icon="ic-20 ic-prev ic-col-white">
                        <v-btn color="primary" depressed :disabled="!isValid || saving" @click.stop="onSave">
                            <KpIcon icon-cls="save" color="white" />
                            {{ $t('save') }}
                        </v-btn>
                        <v-btn color="primary" depressed :disabled="saving" @click.stop="onCancel">
                            <KpIcon icon-cls="x" color="white" />
                            {{ $t('schliessen') }}
                        </v-btn>
                    </v-slide-group>
                </v-toolbar>
            </div>

            <!-- Image -->
            <div v-if="imageConfigForBanner" class="image">
                <h4>{{ $t('titelbild') }}</h4>
                <!-- normal images, not animated -->
                <div v-if="imageRec && !imageRec.animated" class="images-container">
                    <div v-for="(conf, key) in imageConfigForBanner" :key="key" class="image-container">
                        <label class="tgk-paragraph-text">{{ conf.text }}</label>
                        <v-img :src="getImageUrl(record, conf)" :alt="conf.text" :title="conf.text"> </v-img>
                        <div>{{ conf.width }}x{{ conf.height }}px</div>
                    </div>
                </div>
                <!-- animated image -->
                <div v-if="imageRec && imageRec.animated">
                    <label class="tgk-paragraph-text">{{ $t('animated_img') }}</label>
                    <v-img :src="imageRec.url" :alt="imageRec.title" :title="imageRec.title" />
                    <div>{{ imageRec.width }}x{{ imageRec.height }}px</div>
                </div>
                <!-- change btn -->
                <ActionBtn :disabled="!record.id" class="mt-2" @click.stop="imgSelectDlg = true">
                    <KpIcon icon-cls="dashboard" color="white" />
                    {{ $t('change_titleimg') }}
                </ActionBtn>
            </div>

            <!-- Main content -->
            <div class="content">
                <v-form ref="form" v-model="isValid">
                    <v-container fluid>
                        <!-- Bezeichnung -->
                        <v-row dense>
                            <v-col>
                                <LabelField :label="$t('bezeichnung')">
                                    <v-text-field
                                        v-model="record.title"
                                        autofocus
                                        dense
                                        outlined
                                        hide-details="auto"
                                        :rules="[rules.required]"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>

                        <!-- Publikation -->
                        <v-row v-if="needsPublicationFields" dense>
                            <v-col>
                                <LabelField :label="$t('Publizieren')">
                                    <div class="d-flex" style="gap: 10px">
                                        <DateTextfield
                                            :value="record.publish_from"
                                            :placeholder="$t('pub_from')"
                                            hide-details="auto"
                                            :rules="[rules.required]"
                                            @input="onDateInput('publish_from', $event)"
                                        />
                                        <DateTextfield
                                            hide-details="auto"
                                            :value="record.publish_to"
                                            :placeholder="$t('pub_to')"
                                            :rules="[rules.required]"
                                            @input="onDateInput('publish_to', $event)"
                                        />
                                    </div>
                                </LabelField>
                            </v-col>
                        </v-row>

                        <v-row dense>
                            <!-- Platzhalter -->
                            <v-col v-if="needsPlaceholderField" cols="6">
                                <v-checkbox
                                    v-model="record.is_placeholder"
                                    :label="`${$t('placeholder')}: ${$t('placeholder_text')}`"
                                    hide-details="auto"
                                />
                            </v-col>
                            <!-- Kommerziell -->
                            <v-col v-if="needsCommercialField" cols="6">
                                <v-checkbox
                                    v-model="record.commercial"
                                    :label="$t('kommerziell')"
                                    hide-details="auto"
                                />
                            </v-col>
                        </v-row>

                        <!-- Angaben für Icon-Banner -->
                        <v-row v-if="needsIconField(record) || needsContextField(record)" dense>
                            <v-col v-if="needsIconField(record)" cols="6">
                                <LabelField :label="$t('icon')">
                                    <banner-icon-select
                                        v-model="record.icon"
                                        hide-details="auto"
                                        :placeholder="$t('icon')"
                                    />
                                </LabelField>
                            </v-col>
                            <v-col v-if="needsContextField(record)" cols="6">
                                <LabelField :label="$t('context')">
                                    <banner-context-select
                                        v-model="record.context"
                                        hide-details="auto"
                                        placeholder="neutral"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>

                        <!-- Web-Link -->
                        <v-row v-if="needsLinkField" dense>
                            <v-col>
                                <LabelField :label="$t('link')">
                                    <UrlField v-model="record.link" placeholder="z.b. https://thurgaukultur.ch/" />
                                </LabelField>
                            </v-col>
                        </v-row>

                        <v-row dense>
                            <!-- comment -->
                            <v-col cols="6">
                                <LabelField :label="$t('comment_additions')">
                                    <v-textarea v-model="record.comment" hide-details="auto" rows="5" dense outlined />
                                </LabelField>
                            </v-col>
                            <!-- Kontaktperson -->
                            <v-col v-if="needsContactField" cols="6">
                                <LabelField :label="$t('contactperson')">
                                    <v-textarea
                                        v-model="record.contact_person"
                                        hide-details="auto"
                                        rows="5"
                                        dense
                                        outlined
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>

                        <!-- weight -->
                        <v-row dense>
                            <v-col v-if="needsWeightField(record)">
                                <LabelField :label="$t('gewichtung')">
                                    <v-slider
                                        v-model="record.weight"
                                        hide-details="auto"
                                        :min="1"
                                        :max="100"
                                        thumb-label
                                        :thumb-size="24"
                                    >
                                        <template #append>
                                            <div>{{ record.weight }}</div>
                                        </template>
                                    </v-slider>
                                </LabelField>
                            </v-col>
                            <!-- click counter -->
                            <v-col v-if="needsClickCounter" cols="6">
                                <LabelField :label="$t('clickcount')">
                                    <v-text-field :value="record.count" readonly dense outlined />
                                </LabelField>
                            </v-col>
                            <!-- display order -->
                            <v-col v-if="needsDisplayOrderField" cols="6">
                                <LabelField :label="$t('display_order')">
                                    <v-text-field v-model="record.display_order" dense outlined type="number" />
                                </LabelField>
                            </v-col>
                        </v-row>

                        <!-- Anzeigeorte -->
                        <v-row v-if="needsLocations(record)" dense>
                            <v-col>
                                <LabelField :label="$t('banner_location')">
                                    <banner-location-select
                                        v-model="record.location_idents"
                                        hide-details="auto"
                                        chips
                                        multiple
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
            </div>
        </v-card>
        <MMSelectDialog
            v-if="imgSelectDlg"
            v-model="imgSelectDlg"
            :image-formats-config="imageConfigForBanner"
            :image-crop-data="record.featured_img_crop_config"
            :initial-image="imageRec"
            :initial-step="imageRec ? 2 : 1"
            @image-select="onImageSelect"
            @image-crop="onImageCrop"
        />
    </div>
</template>

<script>
import dayjs from 'dayjs';
import { mapActions } from 'vuex';
import { formRules, errToStr, setBrowserTitle, shortenString } from '@/helpers/tools';
import { instance as getApi } from '@/helpers/api';
import GlobalsMixin from '@/mixins/globals';
import { Banner, BannerCollection } from '@/models/Banner';
import MMSelectDialog from '@/components/media_manager/SelectFormatDialog.vue';
import BannerContextSelect from '@/components/banner/banner-context-select.vue';
import BannerIconSelect from '@/components/banner/banner-icon-select.vue';
import BannerLocationSelect from '@/components/banner/banner-location-select.vue';
import UrlField from '@/components/form/UrlField.vue';

export default {
    name: 'BannerEdit',
    components: {
        BannerContextSelect,
        BannerIconSelect,
        BannerLocationSelect,
        MMSelectDialog,
        UrlField,
    },
    mixins: [GlobalsMixin],

    data() {
        return {
            cancelDlg: false,
            imgSelectDlg: false,
            saving: false,
            record: null,
            isValid: true,
        };
    },

    computed: {
        imageRec() {
            if (this.record && this.record.featured_image_id) {
                return this.$store.getters['image/imageById'](this.record.featured_image_id);
            } else {
                return null;
            }
        },
        imageConfigForBanner() {
            if (this.record) {
                let conf = this.imageConfig[`banner_${(this.record.type || '').toLowerCase()}`];
                return conf;
            }
            return null;
        },
        needsPublicationFields() {
            // cockpit infoboxes do not need publication fields:
            return (
                this.record &&
                ['infobox_event_submit', 'infobox_event_publish', 'reduction'].indexOf(this.record.type) < 0
            );
        },
        needsPlaceholderField() {
            // cockpit infoboxes do not need placeholder fields:
            return (
                this.record &&
                ['infobox_event_submit', 'infobox_event_publish', 'reduction'].indexOf(this.record.type) < 0
            );
        },
        needsCommercialField() {
            // cockpit infoboxes do not need commercial fields:
            return (
                this.record &&
                ['infobox_event_publish', 'infobox_event_submit', 'reduction'].indexOf(this.record.type) < 0
            );
        },
        needsLinkField() {
            // cockpit infoboxes do not need link fields:
            return this.record && ['infobox_event_submit', 'infobox_event_publish'].indexOf(this.record.type) < 0;
        },
        needsContactField() {
            // cockpit infoboxes do not need contact fields:
            return (
                this.record &&
                ['infobox_event_submit', 'infobox_event_publish', 'reduction'].indexOf(this.record.type) < 0
            );
        },
        needsClickCounter() {
            // cockpit infoboxes do not need click counter fields:
            return (
                this.record &&
                ['infobox_event_submit', 'infobox_event_publish', 'reduction'].indexOf(this.record.type) < 0
            );
        },
        needsDisplayOrderField() {
            // cockpit infoboxes do need display order fields:
            return this.record && ['infobox_event_submit', 'infobox_event_publish'].indexOf(this.record.type) >= 0;
        },
        browserTitle() {
            const parts = [];
            if (this.record?.title) {
                parts.push(shortenString(this.record?.title, 30), this.activeNavItem?.title);
            } else {
                parts.push(this.$t('create_entry'));
            }
            parts.push(this.$t('banner'));
            return parts.filter(Boolean).join(' - ');
        },
    },

    watch: {
        record(act) {
            if (act && act.featured_image_id) {
                this.loadImage({ imageId: act.featured_image_id });
            }
        },
        browserTitle: {
            handler(newValue) {
                setBrowserTitle(newValue);
            },
            immediate: true,
        },
    },

    created() {
        this.rules = formRules;
        this.imageConfig = this.globalVar('featured_images_config') || {};
        this.cacheBuster = new Date().getTime();
    },

    mounted() {
        this.loadRecord(this.$route.params.id);
    },

    methods: {
        ...mapActions(['runAsync']),
        ...mapActions('image', ['loadImage']),
        async loadRecord(id) {
            // create a new record if requested, or load an existing one:
            if (id === 'new' || !Number(id)) {
                this.record = new Banner({
                    type: this.$route.query.type || 'commercial_large',
                });
                // premature return in case of new record:
                this.validateForm();
                return;
            }

            // load existing record:
            this.record = null;
            try {
                const collection = new BannerCollection();
                await this.runAsync(() => collection.query({ id: id }));
                const record = collection.first();
                if (record) {
                    this.record = record;
                }
                this.validateForm();
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            }
        },

        async validateForm() {
            await this.$nextTick();
            this.$refs.form && this.$refs.form.validate();
        },

        onDateInput(field, value) {
            this.record.set(field, dayjs(value));
        },

        async onSave() {
            if (!this.record) {
                return;
            }

            this.saving = true;

            // nothing to do, so don't save:
            if (!this.record.changed()) {
                this.saving = false;
                return;
            }

            try {
                let isNew = !this.record.id;
                await this.runAsync(() => this.record.save());
                if (isNew) {
                    // when a new rec is created, replace the route...
                    this.$router.replace({ name: 'banner-edit', params: { id: this.record.id } });
                }
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.saving = false;
            }
        },

        onCancel() {
            if (window.history.length > 2) {
                this.$router.back();
            } else {
                this.$router.push({ name: 'banner' });
            }
        },

        onFeaturedImagesCreated() {
            this.onSave();
        },

        needsIconField(banner) {
            return ['topper', 'celebrity_tip', 'content_tip', 'reduction'].indexOf(banner.type) >= 0;
        },

        needsContextField(banner) {
            return ['topper', 'author_tip', 'celebrity_tip', 'content_tip'].indexOf(banner.type) >= 0;
        },
        needsWeightField(banner) {
            return ['commercial_large', 'commercial_small'].indexOf(banner.type) >= 0;
        },
        needsLocations(banner) {
            return (
                [
                    'commercial_large',
                    'commercial_small',
                    'topper',
                    'author_tip',
                    'celebrity_tip',
                    'content_tip',
                ].indexOf(banner.type) >= 0
            );
        },

        getImageUrl(banner, imageConf) {
            let path = `banner_${banner.type}/${banner.id}/${imageConf.ident}.${imageConf.format}`;
            let fullpath = `${this.globalVar('imageroot')}/${path}?ts=${this.cacheBuster}`;
            return fullpath;
        },

        async onImageSelect(image) {
            if (image) {
                await this.onImageCrop({}, image);
            }
        },

        async onImageCrop(cropData, image) {
            if (image) {
                try {
                    this.imgSelectDlg = false;
                    this.saving = true;
                    await this.createFeaturedImage(cropData, image, this.record);
                    this.record.set({
                        featured_image_id: image.id,
                        featured_img_crop_config: cropData || null,
                    });
                    this.cacheBuster = new Date().getTime();
                    this.loadImage({ imageId: image.id });
                    await this.onSave();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.saving = false;
                }
            }
        },

        async createFeaturedImage(cropData, image, record) {
            await getApi().post('/image/createFeaturedImages', {
                image_id: image.id,
                record_id: record.id,
                type: `banner_${record.type}`,
                crop_data: cropData ? JSON.stringify(cropData) : null,
            });
        },

        getBrowserTitle() {
            return this.browserTitle;
        },
    },
};
</script>

<style lang="scss" scoped>
.main-grid {
    display: grid;
    grid-template-rows: auto 1fr;
    grid-template-columns: 1fr auto;
    grid-template-areas:
        'header header'
        'content image';
}

.top-header {
    grid-area: header;
}
.image {
    grid-area: image;
    background-color: var(--toolbar-bg-color);
    padding: 12px;
    width: 300px;
}
.content {
    grid-area: content;
    margin: 10px;
    overflow: auto;
}
</style>
